import { useMemo } from "react";
import type { HarmonicChannel } from "@/lib/types";
import { BarChartUplot } from "@/components/BarChartUplot";

// Harmonic spectrum for one channel — H2..H50 as a percentage of the
// fundamental, the canonical IEC harmonic-distortion view. H1 itself is
// excluded from the bars (it would dwarf them) and called out in the header
// alongside THD. NOT the same as interharmonics — see InterharmonicChart.

export function HarmonicSpectrumChart({ channel }: { channel: HarmonicChannel }) {
  const mag = channel.harmonics_mag;

  const spectrum = useMemo(() => {
    if (!mag || mag.length < 50 || mag[0] <= 0) return null;
    const h1 = mag[0];
    // Orders 2..50 as % of the fundamental.
    const x: number[] = [];
    const pct: number[] = [];
    for (let h = 2; h <= 50; h++) {
      x.push(h);
      pct.push((mag[h - 1] / h1) * 100);
    }
    return { x, pct, h1 };
  }, [mag]);

  if (channel.harmonic_analysis_skipped_reason) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Harmonic analysis skipped — {channel.harmonic_analysis_skipped_reason}
      </p>
    );
  }
  if (!spectrum) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No usable spectrum (fundamental is zero).
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
        <span className="text-muted-foreground">
          Fundamental (H1):{" "}
          <span className="font-mono text-foreground">
            {spectrum.h1.toFixed(2)} {channel.channel_unit} RMS
          </span>
        </span>
        <span className="text-muted-foreground">
          THD (H2–H50):{" "}
          <span className="font-mono text-foreground">
            {channel.thd_pct != null ? `${channel.thd_pct.toFixed(2)}%` : "—"}
          </span>
        </span>
        {channel.window_unsynchronized && (
          <span className="text-amber-400 text-xs">
            window not fundamental-synchronised — spectral leakage possible
          </span>
        )}
      </div>
      <BarChartUplot
        x={spectrum.x}
        bars={{ label: "% of fundamental", values: spectrum.pct, color: "#38bdf8" }}
        height={240}
        xLabel="harmonic order"
        yLabel="% of H1"
      />
    </div>
  );
}
